import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";

import { ReactComponent as LeftArrow } from "../../resources/icons/left_arrow.svg";
import downArrow from "./../../resources/down_arrow.png";

import DocumentShow from "../elements/DocumentShow";
import Modal from "../elements/Modal";
import MultiImageUpload from "../elements/MultiImageUpload";
import SubtaskList from "../elements/SubtaskList";
import { Project } from "../../types";
import ProjectDataSection from "../elements/ProjectDataSection";
import ProjectParticipants from "../elements/participants/ProjectParticipants";
import { getStatusStyle } from "../../utils/helperFunctions";
import { getProjectById, getDocumentPresignedUrl } from "../../api/projects";
import PDFPreviewModal from "../elements/modals/PDFPreviewModal";

const documentTypes = [
  { title: "Building Permit", key: "PERMIT" },
  { title: "AKT 14", key: "AKT14" },
  { title: "AKT 15", key: "AKT15" },
  { title: "AKT 16", key: "AKT16" },
  { title: "Other Documents", key: "OTHER" },
];

const ProjectPublishPage: React.FC = () => {
  const { id: projectId } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);
  const [documents, setDocuments] = useState<Record<string, any[]>>({});
  const [openDocs, setOpenDocs] = useState(true);

  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);

  const [summary, setSummary] = useState("");
  const [images, setImages] = useState<File[]>([]);

  const [uploadStatus, setUploadStatus] = useState<
    "" | "uploading" | "complete" | "failed"
  >("");
  const [uploadError, setUploadError] = useState<string>("");

  const [previewUrl, setPreviewUrl] = useState("");
  const [previewName, setPreviewName] = useState("");
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  const handlePreview = async (docId: string | number, name: string) => {
    const { data, error } = await getDocumentPresignedUrl(docId);
    if (error || !data?.file?.url) {
      alert("Error loading document preview.");
      return;
    }
    setPreviewUrl(data.file.url);
    setPreviewName(decodeURIComponent(name));
    setIsPreviewOpen(true);
  };

  const fetchProject = useCallback(async () => {
    if (projectId) {
      setLoading(true);
      const { data, error } = await getProjectById(parseInt(projectId));
      if (error) {
        navigate("/projects");
      }
      if (data) {
        setProject(data);
        const grouped = (data.documents || []).reduce((acc: any, doc: any) => {
          acc[doc.type] = acc[doc.type] || [];
          acc[doc.type].push(doc);
          return acc;
        }, {} as Record<string, any[]>);
        setDocuments(grouped);
      }
      setLoading(false);
    }
  }, [projectId, navigate]);

  useEffect(() => {
    fetchProject();
  }, [fetchProject, projectId]);

  useEffect(() => {
    if (
      !loading &&
      project &&
      (project.status.toLowerCase() === "completed" ||
        project.status.toLowerCase() === "cancelled")
    ) {
      navigate(`/projects/${projectId}/finished`, { replace: true });
    }
  }, [loading, project, navigate, projectId]);

  if (loading) return <div>Loading…</div>;
  if (!project) return <div>Project not found</div>;

  const isDraft = project.status.toLowerCase() === "draft";
  const canCompleteOrCancel = ["open", "ongoing"].includes(
    project.status.toLowerCase()
  );

  const handlePublish = async () => {
    const token = localStorage.getItem("token");
    await axios.put(
      `${process.env.REACT_APP_URL}/projects/${projectId}`,
      { status: "OPEN" },
      { headers: { Authorization: `Bearer ${token}`, "X-Client-Type": "web" } }
    );
    await fetchProject();
  };

  const resetModalState = () => {
    setSummary("");
    setImages([]);
    setUploadStatus("");
    setUploadError("");
    setShowCancelModal(false);
    setShowCompleteModal(false);
  };

  const uploadImages = async () => {
    const token = localStorage.getItem("token");
    const uploadedUrls: string[] = [];
    for (const img of images) {
      const formData = new FormData();
      formData.append("file", img);
      const res = await axios.post(
        `${process.env.REACT_APP_URL}/projects/${projectId}/closure-images`,
        formData,
        {
          headers: { Authorization: `Bearer ${token}`, "X-Client-Type": "web" },
        }
      );
      uploadedUrls.push(res.data.url);
    }
    return uploadedUrls;
  };

  const handleConfirm = async (status: "COMPLETED" | "CANCELLED") => {
    if (!summary.trim()) return;
    try {
      setUploadStatus("uploading");
      setUploadError("");
      const uploadedUrls = await uploadImages();
      await axios.put(
        `${process.env.REACT_APP_URL}/projects/${projectId}/close`,
        {
          status: status,
          summary: summary,
          closureImageUrls: uploadedUrls,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "X-Client-Type": "web",
          },
        }
      );
      setUploadStatus("complete");
      setTimeout(() => {
        resetModalState();
        navigate(`/projects/${projectId}/finished`, { replace: true });
      }, 3000);
    } catch (error: any) {
      setUploadStatus("failed");
      setUploadError(error.response?.data?.error || error.message);
    }
  };

  return (
    <div className="flex">
      <div className="flex-1 p-6 ml-[17.5rem] flex flex-col">
        <div className="flex items-center mb-4 border-b pb-4">
          <button onClick={() => navigate("/projects")} className="mr-3">
            <LeftArrow className="w-6 h-6" style={{ color: "#344054" }} />
          </button>
          <h1 className="text-2xl font-bold">{project.title}</h1>
          <span
            className={`ml-4 text-sm font-medium px-3 py-1 border rounded-full ${getStatusStyle(
              project.status
            )}`}
          >
            {project.status[0].toUpperCase() +
              project.status.slice(1).toLowerCase()}
          </span>
        </div>

        <div className="flex justify-between gap-6 mb-6">
          <div className="flex flex-1">
            <div>
              <h2 className="text-lg font-semibold mb-1">
                Project details summary
              </h2>
              <p className="max-w-[500px] max-h-[100px] overflow-auto pr-2 whitespace-pre-wrap break-words text-[#475467]">
                {project.summary}
              </p>
              <ProjectParticipants
                supervisor={project.supervisor || undefined}
                contractor={project.contractor || undefined}
                assignor={project.assignor}
              />
            </div>
          </div>
          <ProjectDataSection project={project} />
        </div>

        <div
          className="flex items-center cursor-pointer mb-4"
          onClick={() => setOpenDocs(!openDocs)}
        >
          <h2 className="font-semibold">Documents</h2>
          <img
            src={downArrow}
            alt="Toggle"
            className={`w-5 h-5 ml-2 transform ${openDocs ? "" : "rotate-180"}`}
          />
        </div>

        {openDocs &&
          documentTypes.map(({ title, key }) => (
            <DocumentShow
              key={key}
              title={title}
              docs={documents[key] || []}
              onPreview={(docId, name) => handlePreview(docId, name)}
            />
          ))}

        <PDFPreviewModal
          url={previewUrl}
          name={previewName}
          open={isPreviewOpen}
          onClose={() => setIsPreviewOpen(false)}
        />

        <p className="text-xl my-4">Subtasks</p>
        <SubtaskList subtasks={project.subTasks || []} />

        <div className="flex gap-4 mt-auto pt-4">
          {!isDraft && canCompleteOrCancel && (
            <button
              onClick={() => {
                setShowCancelModal(true);
                setSummary("");
                setImages([]);
                setUploadStatus("");
                setUploadError("");
              }}
              className="mr-auto text-red-600 px-4 py-2 rounded"
            >
              Set to Cancelled
            </button>
          )}

          <button
            onClick={() => navigate(`/projects/${projectId}/edit`)}
            className="bg-gray-300 text-gray-700 px-4 py-2 rounded"
          >
            Edit
          </button>

          {isDraft ? (
            <button
              onClick={handlePublish}
              className="bg-buttonBlue text-white px-4 py-2 rounded"
            >
              Publish
            </button>
          ) : canCompleteOrCancel ? (
            <button
              onClick={() => {
                setShowCompleteModal(true);
                setSummary("");
                setImages([]);
                setUploadStatus("");
                setUploadError("");
              }}
              className="bg-buttonBlue text-white px-4 py-2 rounded"
            >
              Set to Completed
            </button>
          ) : null}
        </div>

        <Modal isOpen={showCompleteModal}>
          <div className="p-6 w-full max-h-[80vh] flex flex-col">
            <h2 className="text-lg font-bold">Set project to Completed</h2>
            <p className="text-sm text-gray-500">
              Add summary and images to continue
            </p>

            <div className="flex-1 overflow-y-auto mt-4">
              <div className="mb-4">
                <label className="block mb-2 font-medium">Summary</label>
                <textarea
                  value={summary}
                  onChange={(e) => setSummary(e.target.value)}
                  maxLength={3000}
                  placeholder="Describe the completion summary..."
                  className="w-full p-2 border rounded h-32 resize-none"
                />
                <div className="text-right text-xs text-gray-500">
                  {summary.length}/3000 characters
                </div>
              </div>

              <div className="mb-4">
                <MultiImageUpload
                  files={images}
                  setFiles={setImages}
                  maxFiles={50}
                  uploadStatus={uploadStatus}
                  uploadError={uploadError}
                />
              </div>
            </div>

            <div className="mt-4 flex flex-col gap-2">
              <button
                onClick={() => handleConfirm("COMPLETED")}
                disabled={
                  !summary.trim() ||
                  uploadStatus === "uploading" ||
                  images.length === 0
                }
                className={`
                  w-full px-4 py-2 rounded-lg 
                  ${
                    summary.trim() &&
                    uploadStatus !== "uploading" &&
                    images.length
                      ? "bg-buttonBlue text-white "
                      : "bg-gray-300 text-gray-500 cursor-not-allowed"
                  }
                `}
              >
                Set to Completed
              </button>
              <button
                onClick={resetModalState}
                className="w-full border border-gray-300 text-gray-700 px-4 py-2 rounded-lg"
              >
                Cancel
              </button>
            </div>
          </div>
        </Modal>

        <Modal isOpen={showCancelModal}>
          <div className="p-6 w-full max-h-[80vh] flex flex-col">
            <h2 className="text-lg font-bold">Set project to cancelled</h2>
            <p className="text-sm text-gray-500">
              Add summary and images to continue
            </p>

            <div className="flex-1 overflow-y-auto mt-4">
              <div className="mb-4">
                <label className="block mb-2 font-medium">Summary</label>
                <textarea
                  value={summary}
                  onChange={(e) => setSummary(e.target.value)}
                  maxLength={3000}
                  placeholder="Describe why your project was cancelled..."
                  className="w-full p-2 border rounded h-32 resize-none"
                />
                <div className="text-right text-xs text-gray-500">
                  {summary.length}/3000 characters
                </div>
              </div>

              <div className="mb-4">
                <MultiImageUpload
                  files={images}
                  setFiles={setImages}
                  maxFiles={50}
                  uploadStatus={uploadStatus}
                  uploadError={uploadError}
                />
              </div>

              {uploadStatus === "uploading" && (
                <p className="text-sm text-gray-500 mb-2">Uploading...</p>
              )}
              {uploadStatus === "complete" && (
                <p className="text-sm text-green-600 mb-2">Upload complete!</p>
              )}
              {uploadStatus === "failed" && (
                <div className="mb-2">
                  <p className="text-sm text-red-600">Upload failed</p>
                  {uploadError && (
                    <p className="text-xs text-red-600">{uploadError}</p>
                  )}
                </div>
              )}
            </div>

            <div className="mt-4 flex flex-col gap-2">
              <button
                onClick={() => handleConfirm("CANCELLED")}
                disabled={
                  !summary.trim() ||
                  uploadStatus === "uploading" ||
                  images.length === 0
                }
                className={`
                  w-full px-4 py-2 rounded-lg 
                  ${
                    summary.trim() &&
                    uploadStatus !== "uploading" &&
                    images.length
                      ? "bg-red-600 text-white"
                      : "bg-gray-300 text-gray-500 cursor-not-allowed"
                  }
                `}
              >
                Set to Cancelled
              </button>
              <button
                onClick={resetModalState}
                className="w-full border border-gray-300 text-gray-700 px-4 py-2 rounded-lg"
              >
                Cancel
              </button>
            </div>
          </div>
        </Modal>
      </div>
    </div>
  );
};

export default ProjectPublishPage;
